import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl'
};

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  className = ''
}) => {
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Handle scroll locking
  useEffect(() => {
    const dialogElement = dialogRef.current;
    if (!dialogElement) return;

    if (isOpen) {
      disableBodyScroll(dialogElement, {
        reserveScrollBarGap: true,
      });
    }

    return () => {
      if (dialogElement) {
        enableBodyScroll(dialogElement);
      }
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const dialogContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 overflow-y-auto z-[100]">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40"
            style={{ willChange: 'opacity' }}
            aria-hidden="true"
          />
          
          {/* Dialog positioning container */}
          <div className="fixed inset-0 flex items-center justify-center p-4" onClick={onClose}>
            {/* Dialog panel */}
            <motion.div
              ref={dialogRef}
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              style={{ willChange: 'transform, opacity' }}
              className={`relative w-full ${sizeClasses[size]} mx-auto ${className}`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                className="absolute -top-3 -right-3 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-lg text-gray-500 hover:text-gray-700 z-10
                         border border-gray-100 transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>
              {children}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );

  // Use portal to render at document root level to avoid container issues
  return typeof document !== 'undefined' 
    ? createPortal(dialogContent, document.body)
    : null;
}; 